import React, { useState, useEffect } from 'react';
import { triggerInstall, isInstalled, isInstallable, isOnline, activateWaitingSW } from '../utils/pwa';

import Ikon from './Ikon';
import { BRAND } from '../utils/brand';

export function PWAInstallBanner() {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('ft-install-dismissed') === 'true');
  // Prompt bawaan browser tidak selalu ada: iOS Safari tidak punya sama sekali,
  // browser dalam aplikasi (WhatsApp/IG) juga tidak. Dulu tombol Install dalam
  // keadaan itu diam saja DAN banner-nya tidak ikut tertutup — terlihat seperti
  // popup yang nyangkut. Sekarang berganti jadi panduan manual.
  const [manual, setManual] = useState(false);

  useEffect(() => {
    if (isInstalled() || dismissed) return;

    const handleInstallable = () => setShow(true);
    window.addEventListener('ft-installable', handleInstallable);

    // beforeinstallprompt bisa menyala sebelum komponen ini terpasang, dan event
    // yang sudah lewat tidak pernah menyala ulang — jadi ditanyakan sekali di sini.
    if (isInstallable()) setShow(true);

    // Begitu aplikasinya benar-benar terpasang, banner harus hilang saat itu juga.
    const handleInstalled = () => setShow(false);
    window.addEventListener('ft-installed', handleInstalled);

    return () => {
      window.removeEventListener('ft-installable', handleInstallable);
      window.removeEventListener('ft-installed', handleInstalled);
    };
  }, [dismissed]);

  const handleInstall = () => {
    if (triggerInstall()) setShow(false);
    else setManual(true);
  };

  const handleDismiss = () => {
    setShow(false);
    setDismissed(true);
    localStorage.setItem('ft-install-dismissed', 'true');
  };

  if (!show) return null;

  if (manual) {
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md animate-slide-up">
        <div className="overflow-hidden bg-white border shadow-2xl rounded-2xl border-brand-100">
          <div className="p-1 bg-gradient-brand" />
          <div className="p-4">
            <p className="text-sm font-bold font-display text-slate-800">Pasang lewat menu browser</p>
            <p className="mt-1 text-xs leading-relaxed text-slate-500">
              Browser ini tidak menyediakan tombol pasang otomatis. Buka menu browser
              (ikon <span className="font-semibold">⋮</span> atau <span className="font-semibold">Bagikan</span>),
              lalu pilih <span className="font-semibold">Install app</span> atau{' '}
              <span className="font-semibold">Tambahkan ke Layar Utama</span>.
            </p>
            <button onClick={handleDismiss} className="w-full px-4 py-2 mt-3 text-xs font-bold text-white transition-all bg-brand-600 hover:bg-brand-700 rounded-xl active:scale-95">
              Mengerti
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md animate-slide-up">
      <div className="overflow-hidden bg-white border shadow-2xl rounded-2xl border-brand-100">
        <div className="p-1 bg-gradient-brand" />
        <div className="flex items-center gap-4 p-4">
          <div className="flex items-center justify-center overflow-hidden shadow-lg w-14 h-14 rounded-2xl shrink-0 bg-gradient-brand">
            <img src={BRAND.logo} alt={BRAND.nama} className="object-cover w-full h-full" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold font-display text-slate-800">Install {BRAND.nama}</p>
            <p className="text-xs text-slate-500 mt-0.5 leading-tight">Tambahkan ke layar utama untuk akses lebih cepat & mode offline</p>
          </div>
          <div className="flex flex-col gap-1.5 shrink-0">
            <button onClick={handleInstall} className="px-4 py-2 text-xs font-bold text-white transition-all bg-brand-600 hover:bg-brand-700 rounded-xl active:scale-95 whitespace-nowrap">
              Install App
            </button>
            <button onClick={handleDismiss} className="text-xs font-medium text-center transition-colors text-slate-400 hover:text-slate-600">
              Nanti saja
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── SW Update Banner ──────────────────────────────────────────
export function PWAUpdateBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const handleUpdate = () => setShow(true);
    window.addEventListener('ft-sw-update', handleUpdate);
    return () => window.removeEventListener('ft-sw-update', handleUpdate);
  }, []);

  const handleUpdate = () => {
    setShow(false);
    activateWaitingSW();
  };

  if (!show) return null;

  return (
    <div className="fixed z-50 top-4 right-4 animate-slide-up">
      <div className="flex items-center max-w-xs gap-3 p-4 bg-white border border-green-100 shadow-xl rounded-2xl">
        <div className="flex items-center justify-center w-10 h-10 text-xl bg-green-100 rounded-xl shrink-0"><Ikon e="🔄" /></div>
        <div className="flex-1">
          <p className="text-sm font-bold text-slate-800">Update Tersedia!</p>
          <p className="text-xs text-slate-500">Versi terbaru siap digunakan</p>
        </div>
        <button onClick={handleUpdate} className="px-3 py-2 text-xs font-bold text-white transition-all bg-green-600 hover:bg-green-700 rounded-xl shrink-0">
          Update
        </button>
      </div>
    </div>
  );
}

// ── Offline Indicator ─────────────────────────────────────────
export function OfflineIndicator() {
  const [online, setOnline] = useState(isOnline());

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  if (online) return null;

  return (
    <div className="fixed z-50 bottom-4 right-4 animate-slide-up">
      <div className="flex items-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-red-600 shadow-xl rounded-2xl">
        <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
        Offline — Data mungkin tidak terkini
      </div>
    </div>
  );
}

export default function PWAShell() {
  return (
    <>
      <PWAInstallBanner />
      <PWAUpdateBanner />
      <OfflineIndicator />
    </>
  );
}
